import TextInput from 'components/text-input';
import Button from 'components/button';
import Icon from 'components/icon';
import ContentWrapper from 'components/content-wrapper';

import React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from "react";

const TravelCalculatorBlock = ({
    latitude,
    longitude,
    inputBox,
    routesApiEndpoint,
    placesApiEndpoint
}) => {
    const [query, setQuery] = useState("");
    const [previousQuery, setPreviousQuery] = useState("");
    const [placeId, setPlaceId] = useState("");
    const [results, setResults] = useState([]);
    const [times, setTimes] = useState(null);
    const inputRef = useRef();
    const listRef = useRef();
    const calculateTimesFlag = useRef(false);

    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (calculateTimesFlag.current) {
            calculateTimesFlag.current = false;
            calculateTimes();
        }
    }, [placeId, calculateTimesFlag]);

    useEffect(() => {
        if (query.trim().length < 2) {
            setResults([]);
            return;
        }

        if (query == previousQuery) {
            return;
        }

        // Debounce
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchResults(query);
        }, 300);

        return () => clearTimeout(debounceRef.current);
    }, [query]);

    const fetchResults = async (text) => {
        setPreviousQuery(text);
        // Cancel previous request
        if (abortRef.current) {
            abortRef.current.abort();
        }

        abortRef.current = new AbortController();

        try {
            const res = await fetch(
                `${placesApiEndpoint}?input=${encodeURIComponent(text)}`,
                { signal: abortRef.current.signal }
            );

            if (!res.ok) throw new Error("Request failed");

            console.log("Sending autocomplete query");
            const data = await res.json();
            setResults(data.Suggestions); // expects array
        } catch (err) {
            if (err.name !== "AbortError") {
                console.error(err);
            }
        } finally {
        }
    };

    const handleSelect = (text, placeId) => {
        setPreviousQuery(text);
        setQuery(text);
        inputRef.current.value = text;
        setPlaceId(placeId)
        setResults([]);
        focusAndScrollIntoView(inputRef.current);
    };

    const focusAndScrollIntoView = (element) => {
        element.focus();
        element.scrollIntoView({
            block: 'nearest'
        });
    }

    const handleKeyDown = (event) => {
        const focusedElement = document.activeElement;

        if (event.key === 'ArrowDown') {
            if (focusedElement instanceof HTMLInputElement || focusedElement instanceof HTMLUListElement) {
                const ulElement = listRef.current;
                if (ulElement.childElementCount > 0) {
                    const firstListElement = ulElement.firstElementChild;
                    focusAndScrollIntoView(firstListElement);
                    inputRef.current.setAttribute('aria-activedescendant', firstListElement.id);
                    event.preventDefault();
                }
            }
            else if (focusedElement instanceof HTMLLIElement) {
                if (focusedElement.nextElementSibling !== null) {
                    focusAndScrollIntoView(focusedElement.nextElementSibling);
                    inputRef.current.setAttribute('aria-activedescendant', focusedElement.nextElementSibling.id);
                    event.preventDefault();
                }
            }
        }
        else if (event.key === 'ArrowUp') {
            if (focusedElement instanceof HTMLLIElement) {
                if (focusedElement.previousElementSibling !== null) {
                    focusAndScrollIntoView(focusedElement.previousElementSibling);
                    inputRef.current.setAttribute('aria-activedescendant', focusedElement.previousElementSibling.id);
                    event.preventDefault();
                }
                else {
                    const inputElement = inputRef.current;
                    focusAndScrollIntoView(inputElement);
                    inputRef.current.setAttribute('aria-activedescendant', "");
                    event.preventDefault();
                }
            }
        }
        else if (event.key === 'Enter') {
            if (focusedElement instanceof HTMLLIElement) {
                handleSelect(focusedElement.textContent, focusedElement.id);
                calculateTimesFlag.current = true;
                event.preventDefault();
            }
            else if (focusedElement instanceof HTMLButtonElement) {
                calculateTimes();
            }
        }
        else if (event.key === 'Tab') {
            if (focusedElement instanceof HTMLLIElement) {
                handleSelect(focusedElement.textContent, focusedElement.id);
            }
        }
    };

    const calculateTimes = async () => {
        if (!query) return;

        const modes = [
            { key: "TRANSIT", icon: "train", label: "Kollektivt" },
            { key: "DRIVE", icon: "directions_car", label: "Bil" },
            { key: "BICYCLE", icon: "pedal_bike", label: "Sykkel" },
            { key: "WALK", icon: "directions_walk", label: "Gå" },
        ];

        const results = {};


        for (const mode of modes) {
            const url = routesApiEndpoint + `?originPlaceId=${encodeURIComponent(
                placeId ?? query
            )}&destinationLongitude=${encodeURIComponent(
                longitude
            )}&destinationLatitude=${encodeURIComponent(
                latitude
            )}&travelMode=${mode.key}`;

            const res = await fetch(url);
            try {
                const data = await res.json();

                results[mode.label] =
                {
                    icon: mode.icon,
                    duration: data.Duration || "–"
                }
            } catch (e) {
                console.log(e);
                results[mode.label] =
                {
                    icon: mode.icon,
                    duration: "-"
                }
            }

        }

        setTimes(results);
    };

    return (
        <div className="travel-calculator-block">
            <ContentWrapper>
                <div className="travel-calculator-block__titlesection">
                    <h2>Beregn reisetid</h2>
                    <p>
                        Prøv reisetidskalkulatoren for å se hvor lang tid det tar å reise hit fra ditt utgangspunkt, med ulike transportmidler
                    </p>
                </div>
                <div className='travel-calculator-block__input' onKeyDown={handleKeyDown}>
                    {inputBox && (
                        <TextInput
                            onChange={(e) => setQuery(e.target.value)}
                            value={query}
                            defaultValue={query}
                            useValue={false}
                            ref={inputRef}
                            ariaAttributes={{
                                role: "combobox",
                                'aria-auto-complete': "list",
                                'aria-expanded': false
                            }}
                            {...inputBox}
                        />
                    )}

                    {results.length > 0 && (
                        <div className='travel-calculator-block__input-dropdown-list'>
                            <ul tabIndex={0} role="listbox" ref={listRef}>
                                {results.map((item, index) => (
                                    <li tabIndex="0"
                                        role="option"
                                        id={item.PlacePrediction.PlaceId}
                                        key={index}
                                        onClick={() => handleSelect(item.PlacePrediction.Text.Text, item.PlacePrediction.PlaceId)}
                                    >
                                        {item.PlacePrediction.Text.Text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <Button
                        onClick={calculateTimes}
                        text="BEREGN"
                        type='button'
                        theme={Button.themes.black}
                        alignment='center'
                    />
                </div>

                {times && (<div className='travel-calculator-block__result'>
                    <div className='travel-calculator-block__result-list'>
                        {
                            Object.entries(times).map(([label, value]) => (
                                <div className='travel-calculator-block__result-list-entry' key={label}>
                                    <Icon
                                        className='travel-calculator-block__result-list-entry-icon'
                                        name={value.icon}
                                    />
                                    <div tabIndex={0} className='travel-calculator-block__result-list-entry-text'>
                                        <h3>{label}</h3>
                                        <p>{value.duration}</p>
                                    </div>
                                </div>
                            ))
                        }
                    </div>

                    <div className='travel-calculator-block__result-disclaimer'>
                        <p tabIndex={0}>
                            Reisetid er beregnet med Google Maps
                        </p>
                    </div>
                </div>
                )}
            </ContentWrapper>
        </div>
    );
};

TravelCalculatorBlock.propTypes =
{
    latitude: PropTypes.string,
    longitude: PropTypes.string,
    inputBox: PropTypes.exact(TextInput.propTypes),
    routesApiEndpoint: PropTypes.string,
    placesApiEndpoint: PropTypes.string
};

export default TravelCalculatorBlock;